import { useState, useEffect } from "react";
import { Link } from "wouter";
import { usePageTitle } from "@/hooks/use-page-title";
import { Button } from "@/components/ui/button";
import { Plus, Pencil, Trash2, ExternalLink } from "lucide-react";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

interface BlogPost {
  id: number;
  title: string;
  slug: string;
  excerpt: string | null;
  status: string;
  publishedAt: string | null;
  createdAt: string;
}

function StatusBadge({ status }: { status: string }) {
  const isPublished = status === "published";
  return (
    <span style={{
      display: "inline-flex",
      alignItems: "center",
      padding: "0.125rem 0.625rem",
      borderRadius: "9999px",
      fontSize: "0.75rem",
      fontWeight: 600,
      background: isPublished ? "#10b98120" : "#6b728020",
      color: isPublished ? "#10b981" : "#6b7280",
    }}>
      {isPublished ? "Published" : "Draft"}
    </span>
  );
}

export default function AdminBlog() {
  usePageTitle("Admin – Blog");
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deleting, setDeleting] = useState<number | null>(null);

  const fetchPosts = () => {
    setLoading(true);
    setError("");
    fetch(`${BASE}/api/admin/blog`, { credentials: "include" })
      .then(r => r.ok ? r.json() : Promise.reject("Failed to load"))
      .then(data => { setPosts(data); setLoading(false); })
      .catch(() => { setError("Failed to load blog posts"); setLoading(false); });
  };

  useEffect(() => { fetchPosts(); }, []);

  const handleDelete = async (id: number, title: string) => {
    if (!confirm(`Delete "${title}"? This cannot be undone.`)) return;
    setDeleting(id);
    try {
      const res = await fetch(`${BASE}/api/admin/blog/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error("Delete failed");
      setPosts(prev => prev.filter(p => p.id !== id));
    } catch {
      alert("Failed to delete post");
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div style={{ maxWidth: "900px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.5rem" }}>
        <div>
          <h1 style={{ fontSize: "1.75rem", fontWeight: 700, marginBottom: "0.25rem" }}>Blog</h1>
          <p style={{ color: "hsl(var(--muted-foreground))", fontSize: "0.9375rem" }}>
            Manage blog posts for the public blog
          </p>
        </div>
        <Link href="/admin/blog/new">
          <Button style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <Plus style={{ width: "1rem", height: "1rem" }} />
            New Post
          </Button>
        </Link>
      </div>

      {error && (
        <div style={{ padding: "1rem", background: "hsl(var(--destructive) / 0.1)", borderRadius: "0.5rem", color: "hsl(var(--destructive))", marginBottom: "1.5rem" }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ padding: "3rem", textAlign: "center", color: "hsl(var(--muted-foreground))" }}>
          Loading posts…
        </div>
      ) : posts.length === 0 ? (
        <div style={{
          padding: "3rem",
          textAlign: "center",
          border: "1px dashed hsl(var(--border))",
          borderRadius: "0.75rem",
          color: "hsl(var(--muted-foreground))",
        }}>
          <p style={{ marginBottom: "1rem" }}>No blog posts yet.</p>
          <Link href="/admin/blog/new">
            <Button variant="outline">Create your first post</Button>
          </Link>
        </div>
      ) : (
        <div style={{ border: "1px solid hsl(var(--border))", borderRadius: "0.75rem", overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.875rem" }}>
            <thead>
              <tr style={{ background: "hsl(var(--muted) / 0.5)", borderBottom: "1px solid hsl(var(--border))" }}>
                <th style={{ textAlign: "left", padding: "0.75rem 1rem", fontWeight: 600, color: "hsl(var(--muted-foreground))" }}>Title</th>
                <th style={{ textAlign: "left", padding: "0.75rem 1rem", fontWeight: 600, color: "hsl(var(--muted-foreground))" }}>Status</th>
                <th style={{ textAlign: "left", padding: "0.75rem 1rem", fontWeight: 600, color: "hsl(var(--muted-foreground))" }}>Published</th>
                <th style={{ textAlign: "right", padding: "0.75rem 1rem", fontWeight: 600, color: "hsl(var(--muted-foreground))" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {posts.map((post, i) => (
                <tr
                  key={post.id}
                  style={{
                    borderBottom: i < posts.length - 1 ? "1px solid hsl(var(--border))" : undefined,
                    transition: "background 0.1s",
                  }}
                >
                  <td style={{ padding: "0.875rem 1rem" }}>
                    <div style={{ fontWeight: 500 }}>{post.title}</div>
                    <div style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))", marginTop: "0.125rem" }}>
                      /blog/{post.slug}
                    </div>
                  </td>
                  <td style={{ padding: "0.875rem 1rem" }}>
                    <StatusBadge status={post.status} />
                  </td>
                  <td style={{ padding: "0.875rem 1rem", color: "hsl(var(--muted-foreground))" }}>
                    {post.publishedAt
                      ? new Date(post.publishedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
                      : "—"}
                  </td>
                  <td style={{ padding: "0.875rem 1rem" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "0.5rem" }}>
                      {post.status === "published" && (
                        <a
                          href={`${BASE}/blog/${post.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="View public post"
                          style={{ display: "flex", alignItems: "center", padding: "0.375rem", borderRadius: "0.375rem", color: "hsl(var(--muted-foreground))", textDecoration: "none" }}
                        >
                          <ExternalLink style={{ width: "1rem", height: "1rem" }} />
                        </a>
                      )}
                      <Link href={`/admin/blog/${post.id}`}>
                        <button
                          title="Edit post"
                          style={{ display: "flex", alignItems: "center", padding: "0.375rem", borderRadius: "0.375rem", border: "none", background: "none", cursor: "pointer", color: "hsl(var(--muted-foreground))" }}
                        >
                          <Pencil style={{ width: "1rem", height: "1rem" }} />
                        </button>
                      </Link>
                      <button
                        onClick={() => handleDelete(post.id, post.title)}
                        disabled={deleting === post.id}
                        title="Delete post"
                        style={{ display: "flex", alignItems: "center", padding: "0.375rem", borderRadius: "0.375rem", border: "none", background: "none", cursor: deleting === post.id ? "not-allowed" : "pointer", color: "#ef4444", opacity: deleting === post.id ? 0.5 : 1 }}
                      >
                        <Trash2 style={{ width: "1rem", height: "1rem" }} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
